import React, { useCallback, useState } from 'react';
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartPanes,
  ChartPane,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartTitle,
} from '@progress/kendo-react-charts';
import 'hammerjs';
import PageTitle from 'components/PageTitle';
import SearchCriteria from 'components/SearchCriteria';
import ReportContent from 'components/ReportContent';
import { fetchResource } from 'helpers/fetch';
import { API_CONSTANTS } from 'constants/api';
import Grid from '@material-ui/core/Grid';
import DataGrid from 'components/DataGrid';
import './index.css';

export const getPremiumTrendData = async (criteria) => {
  const data = await fetchResource(API_CONSTANTS.REPORT_QUARTERLY_PREMIUM_TREND, {
    method: 'POST',
    data: criteria,
  });
  return data;
};

const defaultColumnDisplay = 18;

const PremiumTrendPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({});
  const [min, setMin] = React.useState(0);
  const [max, setMax] = React.useState(data.length);
  const [step, setStep] = React.useState({
    step: 0,
  });
  const handleSearch = useCallback(async (criteria) => {
    setLoading(true);
    setSearchCriteria(criteria);
    const response = await getPremiumTrendData(criteria);
    setData(response);
    setMax(response.length);
    setMin(0);
    setLoading(false);
  }, []);

  const onSelectEnd = (args) => {
    setMin(args.from);
    setMax(args.to);
    setStep({
      step: Math.floor((args.to - args.from) / 10),
    });
  };

  const defaultTooltipRender = ({ point }) => {
    return (
      <div style={{ padding: '0.25rem' }}>
        <div>Quarter: {point.category}</div>
        <div>Premium: {data[point.categoryIndex].showPremium}</div>
      </div>
    );
  };

  const { startQuarter, startYear, endQuarter, endYear, groupBy } = searchCriteria;

  const premiumTrendHeader = [
    {
      dataColumn: 'quarterYear',
      label: groupBy === 'Q' ? 'Quarter - Year' : 'Year',
      numeric: false,
      sortColumn: 'quarterYear',
    },
    {
      dataColumn: 'showPremium',
      label: 'Premium',
      numeric: true,
      sortColumn: 'premium',
    },
  ];

  return (
    <div style={{ marginBottom: '4rem', width: '100%' }}>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PageTitle>Premium Trend Report</PageTitle>
          </Grid>
          <Grid item xs={12}>
            <SearchCriteria
              onSearch={handleSearch}
              disableSearch={loading}
              showGroupByQuarterAndYear={true}
            />
          </Grid>
          <Grid item xs={12}>
            <ReportContent loading={loading} hasData={data.length > 0}>
              <Chart onSelectEnd={onSelectEnd}>
                <ChartTitle
                  text={
                    groupBy === 'Q'
                      ? `Premium Trend: ${startYear} Q${startQuarter} through ${endYear} Q${endQuarter}`
                      : `Premium Trend: ${startYear} through ${endYear}`
                  }
                />
                <ChartTooltip render={defaultTooltipRender} />
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    data={data}
                    categoryField="quarterYear"
                    min={min}
                    max={max}
                    majorGridLines={step}
                    majorTicks={step}
                    labels={{
                      step: max - min <= defaultColumnDisplay ? 1 : 4, // Top Y-labels
                      // color: "green"
                    }}
                  />
                </ChartCategoryAxis>
                <ChartPanes>
                  <ChartPane />
                </ChartPanes>
                <ChartValueAxis>
                  <ChartValueAxisItem
                    labels={{
                      // color: "purple",
                      format: '$#,##0.00;$(#,##0.00)',
                    }}
                  />
                </ChartValueAxis>
                <ChartSeries>
                  <ChartSeriesItem
                    type="column"
                    data={data}
                    field="premium"
                    markers={{
                      visible: false,
                    }}
                    categoryField="quarterYear"
                  />
                </ChartSeries>
              </Chart>
            </ReportContent>
          </Grid>
          <Grid item xs={12}>
            {data ? (
              data.length > 0 ? (
                <DataGrid
                  data={data}
                  headers={premiumTrendHeader}
                  defaultOrderBy="quarterYear"
                  defaultOrder="asc"
                ></DataGrid>
              ) : null
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default PremiumTrendPage;
